import {
  DeleteTwoTone,
  EditTwoTone,
  PlusOutlined,
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Button,
  Table,
  Tooltip,
  Form,
  notification,
  PageHeader,
  Switch,
  Select,
  Row,
  Col,
  Tag,
} from "antd";
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import IconNotFound from "../../static/icon/IconNotFound";
import DeleteAction from "../../Tools/DeleteAction";

import { useAuth } from "../../context/auth";
import IconAdvertise from "../../static/icon/IconAdvertise";

const POPUP_TYPES = [
  { value: "register", label: "Register" },
  { value: "home", label: "Home" },
];

const PopupTable = () => {
  const history = useHistory();
  const { accessToken } = useAuth();

  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);

  const [update, setUpdate] = useState(0);
  const [total, setTotal] = useState(0);
  const [popups, setPopups] = useState([]);
  const [search, setSearch] = useState({
    pageValue: history.location.pageValue ?? {},
    searchValue: history.location.searchValue ?? {},
  });

  useEffect(() => {
    form.setFieldsValue(search.searchValue);
  }, [form, search]);

  const [currentPopup, setCurrentPopup] = useState(null);

  useEffect(() => {
    async function getPopups({ pageValue, searchValue }) {
      setLoading(true);

      const url = new URL('/api/popup', window.location);
      if (searchValue.type) url.searchParams.append('type', searchValue.type);
      if (searchValue.active !== undefined && searchValue.active !== null)
        url.searchParams.append('active', searchValue.active);

      if (pageValue.page) url.searchParams.append('page', pageValue.page);
      if (pageValue.size) url.searchParams.append('size', pageValue.size);
      if (pageValue.sort) url.searchParams.append('sort', pageValue.sort);
      if (pageValue.order) url.searchParams.append('order', pageValue.order);

      const res = await fetch(url, {
        headers: { 'X-ACCESS-TOKEN': accessToken },
      });
      if (res.ok) {
        const { total, popups } = await res.json();
        setTotal(total);
        setPopups(popups);
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }

      setLoading(false);
    };

    getPopups(search);
  }, [search, accessToken, update]);

  const columns = [
    {
      title: "Image",
      dataIndex: "imgUrl",
      key: "imgUrl",
      width: "12%",
      align: "center",
      render: (imgUrl) =>
        imgUrl ? (
          <img
            src={`/api/upload/${imgUrl}`}
            alt="popup"
            style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 6 }}
          />
        ) : (
          "-"
        ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: "12%",
      align: "center",
      sorter: () => null,
      render: (type) => (
        <Tag color={type === "register" ? "blue" : "green"}>
          {POPUP_TYPES.find((t) => t.value === type)?.label ?? type}
        </Tag>
      ),
    },
    {
      title: "Title (EN)",
      dataIndex: "titleEn",
      key: "titleEn",
      sorter: () => null,
      render: (titleEn) => (
        <Tooltip title={titleEn}>
          <div className="ellipsis-two w-76">{titleEn}</div>
        </Tooltip>
      ),
    },
    {
      title: "Title (TH)",
      dataIndex: "titleTh",
      key: "titleTh",
      sorter: () => null,
      render: (titleTh) => (
        <Tooltip title={titleTh}>
          <div className="ellipsis-two w-76">{titleTh}</div>
        </Tooltip>
      ),
    },
    {
      title: "Inactive/Active",
      dataIndex: "active",
      key: "active",
      width: "10%",
      align: "center",
      sorter: () => null,
      render: (active, popup) => (
        <Switch
          checked={active}
          onChange={(active) => {
            updatePopupActive(active, popup);
          }}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: "10%",
      render: (_, popup) => (
        <span style={{ whiteSpace: "nowrap" }}>
          <Tooltip placement="bottom" title="Edit" className="mx-1">
            <EditTwoTone
              style={{
                backgroundColor: "#F3F6F9",
                padding: 6,
                borderRadius: 6,
              }}
              onClick={() => {
                history.push({
                  pathname: `/popup/${popup.id}`,
                  searchValue: search.searchValue,
                  pageValue: search.pageValue,
                });
              }}
            />
          </Tooltip>

          {!popup.active && (
            <Tooltip placement="bottom" title="Delete" className="mx-1">
              <DeleteTwoTone
                style={{
                  backgroundColor: "#F3F6F9",
                  padding: 6,
                  borderRadius: 6,
                }}
                onClick={() => setCurrentPopup(popup)}
                twoToneColor="red"
              />
            </Tooltip>
          )}
        </span>
      ),
    },
  ];

  async function updatePopupActive(active, popup) {
    const url = new URL('/api/popup/' + popup.id, window.location);

    const formData = new FormData();
    formData.append('active', String(active));

    const res = await fetch(url, {
      method: 'PATCH',
      headers: { 'X-ACCESS-TOKEN': accessToken },
      body: formData,
    });
    if (res.ok) {
      notification.success({
        message: "Success",
        description: "Successfully saved data.",
      });
    } else {
      notification["error"]({
        message: "Something went wrong !",
        description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
      });
    }

    setUpdate(update + 1);
  };

  return (
    <>
      <div className="flex justify-between items-center mt-2">
        <PageHeader
          className="p-0 w-full"
          title={
            <span className="flex items-center mt-2">
              <IconAdvertise /> &nbsp;&nbsp;Popup Management
            </span>
          }
        />

        <Button
          className="mt-2 flex items-center"
          icon={<PlusOutlined />}
          type="primary"
          shape="round"
          onClick={() =>
            history.push({
              pathname: "/popup/new",
              searchValue: search.searchValue,
              pageValue: search.pageValue,
            })
          }
        >
          New Popup
        </Button>
      </div>
      <div className="content">
        <div className="">
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => setSearch(search => ({
              pageValue: { ...search.pageValue, page: 1 },
              searchValue: values,
            }))}
            onReset={() => form.submit()}
          >
            <Row align="middle" gutter={16}>
              <Col className="gutter-row" span={8}>
                <Form.Item
                  label="Type"
                  name="type"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    allowClear
                    placeholder="All"
                    options={POPUP_TYPES}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={16}>
                <div className="flex">
                  <Button
                    className="w-28"
                    type="primary"
                    shape="round"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                  >
                    Search
                  </Button>
                  <Button
                    className="w-28 ml-2"
                    type="primary"
                    ghost
                    shape="round"
                    htmlType="reset"
                    icon={<SyncOutlined />}
                  >
                    Clear Data
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
      <div className="content">
        <Table
          className="inbox_table"
          locale={{ emptyText: <IconNotFound /> }}
          rowKey={(popup) => popup.id}
          loading={loading}
          columns={columns}
          dataSource={popups}
          pagination={{
            size: "small",
            current: search.pageValue.page,
            pageSize: search.pageValue.size,
            defaultPageSize: 10,
            total,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: (total, range) => ` ${range[0]}-${range[1]} of ${total} `,
          }}
          onChange={(pagination, _, sorter) => setSearch(search => ({
            pageValue: {
              page: pagination.current ?? 1,
              size: pagination.pageSize ?? 10,
              sort: sorter.columnKey,
              order: sorter.order === 'ascend' ? 'asc' : 'desc',
            },
            searchValue: search.searchValue,
          }))}
        />
      </div>

      <DeleteAction
        openModalActionDelete={!!currentPopup}
        name="Popup"
        type="popup"
        editRecord={currentPopup}
        onCancel={() => setCurrentPopup(null)}
        onOk={() => {
          setCurrentPopup(null);
          setUpdate(update + 1);
        }}
      />
    </>
  );
};

export default PopupTable;
